import { markdownPlusToAdf } from '@atlassian/adf-markdown-transformer';

import { fieldTypeToPayload, PARENT_FIELD_ID } from '../common/constants';
import type { FieldTypePayload, InputType } from '../common/types';

export class FieldProcessor {
	/**
	 * Populates the field payload by determining whether to update the `fieldsMap` or `updateMap`
	 * and processing the given value according to the specified field type information.
	 */
	populateFieldPayload(fieldType: string, fieldId: string, fieldValue: unknown): unknown {
		const payloadMapping = this.getPayloadMapping(fieldType);
		const type = payloadMapping?.inputType;
		const payloadStructure = this.getPayload(fieldId, fieldValue, payloadMapping);

		if (payloadMapping?.nullable && fieldValue === null) {
			return null;
		}

		if (type === 'array') {
			return this.constructArrayPayloads(fieldValue, payloadStructure);
		}

		if ((type === 'object' && this.isObject(fieldValue)) || this.isValueNode(fieldValue)) {
			return this.constructPayload(payloadStructure, fieldValue, type);
		}

		return fieldValue;
	}

	private getPayloadMapping(fieldType: string): FieldTypePayload | undefined {
		return fieldTypeToPayload[fieldType];
	}

	private getPayload(
		fieldId: string,
		fieldValue: unknown,
		payloadMapping: FieldTypePayload | undefined,
	): unknown {
		if (fieldId === PARENT_FIELD_ID) {
			const stringValue = this.asText(fieldValue);

			// to unset the parent pass empty object
			if (stringValue === '') {
				return {};
			}

			// otherwise it's id
			return { id: 'VALUE' };
		}

		return payloadMapping?.payload;
	}

	private constructPayload(
		payloadStructure: unknown,
		rawValue: unknown,
		type?: InputType,
	): unknown {
		const value = this.convertToNumberOrValue(rawValue, type);

		if (payloadStructure === null || payloadStructure === undefined) {
			return value;
		}

		const payloadString = JSON.stringify(payloadStructure);

		let result = payloadString;

		// Handle ADF_VALUE replacement
		if (result.includes('"ADF_VALUE"')) {
			const adfValue = markdownPlusToAdf({ markdown: this.asText(value) });
			result = result.replace('"ADF_VALUE"', JSON.stringify(adfValue));
		}

		// Handle VALUE replacement
		if (this.isObject(value)) {
			result = result.replace('"VALUE"', JSON.stringify(value));
		} else {
			const textValue = this.asText(value);
			result = result.replace('"VALUE"', JSON.stringify(textValue));
		}

		return JSON.parse(result);
	}

	private constructArrayPayloads(value: unknown, payloadStructure: unknown): unknown[] {
		const items = Array.isArray(value) ? value : this.convertToArray(value);

		const result: unknown[] = [];

		for (const item of items) {
			if (this.isValueNode(item)) {
				result.push(this.constructPayload(payloadStructure, item));
			}
		}

		return result;
	}

	private convertToNumberOrValue(value: unknown, type?: InputType): unknown {
		const textValue = this.asText(value);

		switch (type) {
			case 'integer': {
				const intValue = this.toIntOrNull(textValue);
				return intValue !== null ? intValue : value;
			}
			case 'float': {
				const floatValue = this.toFloatOrNull(textValue);
				return floatValue !== null ? floatValue : value;
			}
			default:
				return value;
		}
	}

	private convertToArray(value: unknown): unknown[] {
		try {
			const textValue = this.asText(value);
			const parsed = JSON.parse(textValue);

			if (Array.isArray(parsed)) {
				return parsed;
			}

			return [value];
		} catch {
			return [value];
		}
	}

	private isValueNode(value: unknown): boolean {
		// A value node is a primitive value (string, number, boolean, null)
		// but not an object or array
		return (
			typeof value === 'string' ||
			typeof value === 'number' ||
			typeof value === 'boolean' ||
			value === null
		);
	}

	private isObject(value: unknown): boolean {
		return typeof value === 'object' && value !== null && !Array.isArray(value);
	}

	private asText(value: unknown): string {
		if (typeof value === 'string') {
			return value;
		}
		if (value === null || value === undefined) {
			return '';
		}
		if (typeof value === 'number' || typeof value === 'boolean') {
			return String(value);
		}
		if (this.isObject(value) || Array.isArray(value)) {
			return JSON.stringify(value);
		}
		return String(value);
	}

	private toIntOrNull(value: string): number | null {
		const parsed = parseInt(value, 10);

		// Check if it's a valid integer and the string representation matches
		if (!Number.isNaN(parsed) && parsed.toString() === value.trim()) {
			return parsed;
		}

		return null;
	}

	private toFloatOrNull(value: string): number | null {
		const parsed = parseFloat(value);

		if (!Number.isNaN(parsed) && Number.isFinite(parsed)) {
			return parsed;
		}

		return null;
	}
}
